'use client';
import React, { useRef, useState, useEffect } from 'react';
import ToolCard from '@/components/ToolCard';
import AdSlot from '@/components/AdSlot';
import { TOOL_CATEGORIES, CATEGORY_META, getToolsByCategory } from '@/lib/toolRegistry';
import { useTheme } from '@/lib/ThemeContext';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'PDF Editing': (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  'Image Edit': (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  ),
  'Audio Tools': (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
    </svg>
  ),
  Scan: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
    </svg>
  ),
  'Text Tools': (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  ),
};

const CATEGORY_GRADIENTS: Record<string, string> = {
  'PDF Editing': 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
  'Image Edit': 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
  'Audio Tools': 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
  Scan: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
  'Text Tools': 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
};

const CATEGORY_GRADIENTS_DARK: Record<string, string> = {
  'PDF Editing': 'linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(220,38,38,0.12) 100%)',
  'Image Edit': 'linear-gradient(135deg, rgba(59,130,246,0.18) 0%, rgba(37,99,235,0.12) 100%)',
  'Audio Tools': 'linear-gradient(135deg, rgba(245,158,11,0.18) 0%, rgba(217,119,6,0.12) 100%)',
  Scan: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(109,40,217,0.12) 100%)',
  'Text Tools': 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(5,150,105,0.12) 100%)',
};

// Skeleton placeholder for a lazy-loaded category section
function CategorySkeleton({ isDark }: { isDark: boolean }) {
  return (
    <div className="mb-16 animate-pulse">
      <div
        className="h-24 rounded-3xl mb-6"
        style={{
          background: isDark ? 'rgba(30,41,59,0.50)' : 'rgba(255,255,255,0.50)',
          border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.50)',
        }}
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-36 rounded-3xl"
            style={{
              background: isDark ? 'rgba(30,41,59,0.50)' : 'rgba(255,255,255,0.50)',
              border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.50)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// Wrapper that lazy-loads its children when it enters the viewport
function LazySection({
  children,
  isDark,
  placeholder,
}: {
  children: React.ReactNode;
  isDark: boolean;
  placeholder?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // start loading 200px before entering viewport
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {visible ? children : (placeholder ?? <CategorySkeleton isDark={isDark} />)}
    </div>
  );
}

export default function ToolGrid() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="tools" className="py-6 px-4 sm:px-6 relative z-10">
      <div className="max-w-7xl mx-auto">
        {TOOL_CATEGORIES?.map((category, catIndex) => {
          const tools = getToolsByCategory(category);
          const visibleTools = tools?.slice(0, 6);
          const hasMore = tools?.length > 6;
          const meta = CATEGORY_META?.[category];
          const categoryId = category?.toLowerCase()?.replace(/\s+/g, '-');
          const categorySlug = category?.toLowerCase()?.replace(/\s+/g, '-');
          const iconGradient = CATEGORY_GRADIENTS[category] ?? 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)';
          const iconGradientDark = CATEGORY_GRADIENTS_DARK[category] ?? 'linear-gradient(135deg, rgba(14,165,233,0.18) 0%, rgba(2,132,199,0.12) 100%)';
          const iconEl = CATEGORY_ICONS[category];

          const categoryContent = (
            <div key={category} id={categoryId} className="mb-16">
              {/* Premium category header */}
              <div
                className="relative mb-6 p-5 sm:p-6 rounded-3xl overflow-hidden"
                style={{
                  background: isDark ? 'rgba(30,41,59,0.75)' : 'rgba(255,255,255,0.72)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.65)',
                  boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.25)' : '0 4px 24px rgba(37,99,235,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  className="absolute inset-0 pointer-events-none rounded-3xl"
                  style={{
                    background: isDark ? iconGradientDark : iconGradient,
                    opacity: isDark ? 0.5 : 0.30,
                  }}
                />

                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
                      style={{
                        background: isDark ? iconGradientDark : iconGradient,
                        color: meta.color,
                        boxShadow: `0 4px 16px ${meta.color}${isDark ? '33' : '22'}`,
                      }}
                    >
                      {iconEl ?? <span className="text-2xl">{meta.emoji}</span>}
                    </div>

                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap mb-1">
                        <h2
                          className="section-title text-xl sm:text-2xl"
                          style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
                        >
                          {category}
                        </h2>
                        <span
                          className="text-[11px] font-bold px-2.5 py-1 rounded-full"
                          style={{
                            background: `${meta.color}${isDark ? '22' : '14'}`,
                            color: meta.color,
                            border: `1px solid ${meta.color}${isDark ? '40' : '28'}`,
                          }}
                        >
                          {tools?.length} tools
                        </span>
                      </div>
                      <p
                        className="text-sm leading-relaxed max-w-md"
                        style={{ color: isDark ? '#64748b' : '#64748b' }}
                      >
                        {meta.description || `${tools?.length} free browser tools`}
                      </p>
                    </div>
                  </div>

                  {hasMore && (
                    <a
                      href={`/tools/category/${categorySlug}`}
                      className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl hover:scale-105"
                      style={{
                        color: meta.color,
                        background: `${meta.color}${isDark ? '18' : '10'}`,
                        border: `1px solid ${meta.color}${isDark ? '35' : '20'}`,
                      }}
                    >
                      View All ({tools?.length})
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>

              {/* Tool cards grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {visibleTools?.map((tool) => (
                  <ToolCard key={tool?.id} tool={tool} />
                ))}
              </div>

              {/* Mobile "View All" button */}
              {hasMore && (
                <div className="mt-5 flex justify-center sm:hidden">
                  <a
                    href={`/tools/category/${categorySlug}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 hover:scale-105"
                    style={{
                      color: meta.color,
                      background: `${meta.color}${isDark ? '18' : '10'}`,
                      border: `1px solid ${meta.color}${isDark ? '35' : '20'}`,
                    }}
                  >
                    View All {tools?.length} Tools
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              )}

              {catIndex < TOOL_CATEGORIES?.length - 1 && (
                <div className="mt-10">
                  <AdSlot type="horizontal" label="Advertisement" />
                </div>
              )}
            </div>
          );

          // First category renders immediately (above the fold / LCP critical)
          // Subsequent categories are lazy-loaded via IntersectionObserver
          if (catIndex === 0) {
            return categoryContent;
          }

          return (
            <LazySection key={category} isDark={isDark}>
              {categoryContent}
            </LazySection>
          );
        })}
      </div>
    </section>
  );
}
